import React, { useState, useEffect, useMemo } from "react";
import { Modal } from "@mui/material";
import { Close } from "@mui/icons-material";

// Components
import SelectInput from "../../../../commons/Inputs/Select/SelectInput";
import TextInput from "../../../../commons/Inputs/TextInput/TextInput";

// Services
import {
  createCharge,
  updateCharge,
} from "../../../../Services/Payments/PaymentApi";
import {
  getAllTraders,
  getConsumersByCompId,
  getGeneratorsByCompId,
} from "../../../../Services/GNA/GetData";

// Styles
import Styles from "./AssignCharge.module.css";

// --- Constants ---
const UNIT_TYPE_LIST = [
  { id: 1, value: "PER_MW", name: "₹/kWh or ₹/unit" },
  { id: 2, value: "FIXED", name: "Fixed" },
];

const FREQUENCY_LIST = [
  { id: 1, value: "ONE_TIME", name: "User Defined" },
  { id: 2, value: "MONTHLY", name: "Monthly" },
  { id: 3, value: "ANNUAL", name: "Annual" },
];

// UPDATED LIST
const CHARGE_CATEGORY_LIST = [
  { id: 1, label: "Platform", value: "Platform" },
  { id: 2, label: "Trader", value: "Trader" },
  { id: 3, label: "STU", value: "State/STU" },
  { id: 4, label: "ISTS", value: "ISTS" },
  { id: 5, label: "All", value: "ALL" },
];

const INITIAL_FORM_STATE = {
  category: {},
  selectedCharge: {},
  value: "",
  unitType: UNIT_TYPE_LIST[0],
  frequency: FREQUENCY_LIST[0],
  trader: {},
  user: CHARGE_CATEGORY_LIST[0],
};

const AssignCharge = ({
  open,
  handleClose,
  charges,
  editData,
  handleAssignCharges,
  getAllAssignCharges,
}) => {
  // --- State ---
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [userList, setUserList] = useState([]);
  const [traderList, setTraderList] = useState([]);

  // --- Derived State (Memoized) ---

  // Logic: Filter the list of charges based on the selected Category
  const filteredChargeList = useMemo(() => {
    if (!formData.category?.value || formData.category.value === "ALL") {
      return charges || [];
    }
    return (
      charges?.filter((ele) => ele?.type === formData.category.value) || []
    );
  }, [formData.category, charges]);

  // --- Effects ---

  // 1. Fetch Dropdown Data
  useEffect(() => {
    const fetchLists = async () => {
      try {
        // 1. Determine the specific user fetch call based on the environment
        const userListPromise =
          process.env.REACT_APP_ENV === "prod"
            ? getGeneratorsByCompId(16)
            : process.env.REACT_APP_ENV === "qa"
            ? getConsumersByCompId(343)
            : getGeneratorsByCompId(41);

        // 2. Run promises concurrently (Traders is fetched in both cases)
        const [userRes, traderRes] = await Promise.all([
          userListPromise,
          getAllTraders(),
        ]);

        // 3. Update state
        setUserList(userRes?.data?.data || []);
        setTraderList(traderRes?.data?.data || []);
      } catch (error) {
        console.error("Error fetching lists", error);
      }
    };

    fetchLists();
  }, []);

  // 2. Handle Edit Mode / Reset on Open
  useEffect(() => {
    if (open && editData?.id) {
      populateFormForEdit();
    } else if (open) {
      setFormData(INITIAL_FORM_STATE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editData, charges]);

  const populateFormForEdit = () => {
    const selectedCategory =
      CHARGE_CATEGORY_LIST.find((ele) => ele.value === editData?.type) || {};

    // Find the charge object in the master list matching the name
    const selectedChargeName =
      charges?.find((ele) => ele?.name === editData?.name) || {};

    const selectedUnitType =
      UNIT_TYPE_LIST.find((ele) => ele.value === editData?.type) ||
      UNIT_TYPE_LIST[0];

    const selectedFreq =
      FREQUENCY_LIST.find((ele) => ele.value === editData?.frequency) ||
      FREQUENCY_LIST[0];

    setFormData({
      category: selectedCategory,
      selectedCharge: selectedChargeName,
      value: editData?.value || "",
      unitType: selectedUnitType,
      frequency: selectedFreq,
      trader: {}, // Logic to pre-fill trader if applicable
      user: {}, // Logic to pre-fill user if applicable
    });
  };

  // --- Handlers ---

  /**
   * Centralized handler to update state and reset dependent fields
   */
  const handleFieldChange = (field, value) => {
    setFormData((prev) => {
      const newState = { ...prev, [field]: value };

      // 1. If Charge Category changes -> Reset Selected Charge
      if (field === "category") {
        newState.selectedCharge = {};
      }

      // 2. Mutual Exclusivity Logic (Updated for Case Sensitivity)
      // Checks against "Trader" instead of "trader"
      if (field === "category" && value?.value === "Trader") {
        newState.user = {}; // Clear user if switching to trader view
      } else if (field === "category" && value?.value !== "Trader") {
        newState.trader = {}; // Clear trader if switching to non-trader view
      }

      return newState;
    });
  };

  const getPayload = () => ({
    name: formData.selectedCharge?.name,
    value: formData.value,
    frequency: formData.frequency?.value,
    type: formData.unitType?.value,
    trader: formData.trader?.consumers?.[0]?.consumerId || null,
    isAdminCharge: !formData?.selectedCharge?.isEditable,
    toEntityId: formData.user?.consumerId,
    chargeType: formData.category?.value,
  });

  const handleSubmit = async () => {
    try {
      const payload = getPayload();
      console.debug("Create Payload:", payload);

      const res = await createCharge(payload);
      if (res?.data?.code === 200) {
        handleAssignCharges(res?.data?.data?.id);
        handleClose();
      }
    } catch (error) {
      console.error("Error creating charge:", error);
    }
  };

  const handleUpdate = async () => {
    try {
      const payload = {
        newCharge: getPayload(),
        chargeIds: [editData?.id],
        consumerIds:
          editData?.entityType === "Consumer" ? [editData?.entityId] : [],
        generatorIds:
          editData?.entityType === "Generator" ? [editData?.entityId] : [],
      };

      const res = await updateCharge(payload);
      if (res?.data?.code === 200) {
        getAllAssignCharges();
        handleClose();
      }
    } catch (error) {
      console.error("Error updating charge:", error);
    }
  };

  const onSave = () => {
    if (editData?.id) handleUpdate();
    else handleSubmit();
  };

  // --- Render ---
  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="assign-charge-modal"
    >
      <div className={`popupModal ${Styles.chargesAddContainer}`}>
        {/* Header */}
        <div className={`display_flex_row modalHeading ${Styles.headingText}`}>
          <div className={`headingText ${Styles.finalHead}`}>
            {editData?.id ? "Update" : "Assign"} Charge
          </div>
          <Close
            style={{ fontSize: "24px", color: "#777777", cursor: "pointer" }}
            onClick={handleClose}
          />
        </div>

        {/* Body */}
        <div
          className={`display_flex_column_gap_8 ${Styles.selectionContainer}`}
        >
          {/* 1. Category Selection */}
          <SelectInput
            heading="Charge Category"
            example="Select Category"
            list={CHARGE_CATEGORY_LIST}
            value={formData.category}
            onChange={(val) => handleFieldChange("category", val)}
            disabled={!!editData?.id}
          />

          {formData.category?.value === "Trader" ? (
            <SelectInput
              heading="Trader"
              example="Select Trader"
              list={traderList}
              value={formData.trader}
              onChange={(val) => handleFieldChange("trader", val)}
            />
          ) : (
            <SelectInput
              heading="Pay To"
              example="Select User"
              list={userList}
              value={formData.user}
              onChange={(val) => handleFieldChange("user", val)}
            />
          )}

          {/* 2. Specific Charge Selection (Dependent on Category) */}
          <SelectInput
            heading="Charge Name"
            example="Select Charge"
            list={filteredChargeList}
            value={formData.selectedCharge}
            onChange={(val) => handleFieldChange("selectedCharge", val)}
            disabled={!!editData?.id}
          />

          {/* 3. Value Input */}
          <TextInput
            heading="Value"
            example="Eg. 10"
            value={formData.value}
            onChange={(val) => {
              if (val === "" || !isNaN(val)) handleFieldChange("value", val);
            }}
          />

          {/* 4. Unit Type (Fixed/Per Unit) */}
          <SelectInput
            heading="Unit Type"
            example="Select Type"
            list={UNIT_TYPE_LIST}
            value={formData.unitType}
            onChange={(val) => handleFieldChange("unitType", val)}
          />

          {/* 5. Frequency */}
          <SelectInput
            heading="Frequency"
            example="Select Frequency"
            list={FREQUENCY_LIST}
            value={formData.frequency}
            onChange={(val) => handleFieldChange("frequency", val)}
          />

          {/* 6. Entity Selection (Conditional) */}
          {/* Updated check to match CHARGE_CATEGORY_LIST value "Trader" */}

          {/* Footer / Action Button */}
          <div>
            <div
              className={`display_flex_row_center ${Styles.submitButton}`}
              onClick={onSave}
              role="button"
              tabIndex={0}
            >
              {editData?.id ? "Update" : "Submit"}
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default AssignCharge;
